import type { MenuProps } from 'antd';
import { Badge } from 'antd';
import type { ReactNode } from 'react';

import {
  BarChartOutlined,
  BulbOutlined,
  CalendarOutlined,
  HomeOutlined,
  LogoutOutlined,
  ProductOutlined,
  SettingOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { WARNING_COLOR } from '../../constants';
import { UserOrganization, UserRole } from '../../enum';
import { isMobile } from '../../helpers/utils.helper';
import type { AuthResult, CurrentUser } from '../pages/Login/authSlice';

type MenuCtx = {
  currentUser: CurrentUser;
  authResult: AuthResult;
  missedScheduledProductCount: number;
};

type SubItem = { label: string; key: string };

type SideMenuItemDef = {
  key: string;
  label: ReactNode | ((ctx: MenuCtx) => ReactNode);
  icon: ReactNode;
  roles: UserRole[];
  visible?: (ctx: MenuCtx) => boolean;
  hideOnMobile?: boolean;
  children?: (ctx: MenuCtx) => SubItem[];
};

const ALL_ROLES = Object.values(UserRole);
const DEFAULT_ROLES = [UserRole.ORGANIZATION, UserRole.CLINICIAN, UserRole.SITE];

// One row per (item, role variant). Order here is render order: for a given
// user only the rows matching their role survive, in this relative order.
const SIDE_MENU_ITEMS: SideMenuItemDef[] = [
  { key: '/home', label: 'Home', icon: <HomeOutlined />, roles: DEFAULT_ROLES },
  { key: '/clients', label: 'Clients', icon: <UsergroupAddOutlined />, roles: [UserRole.CLINICIAN] },
  {
    key: '/scheduled-products',
    label: (ctx) => (
      <>
        Scheduled Products{' '}
        {ctx.missedScheduledProductCount > 0 && (
          <Badge
            count={ctx.missedScheduledProductCount}
            overflowCount={50}
            style={{ backgroundColor: WARNING_COLOR }}
            title="Missed scheduled products count"
          />
        )}
      </>
    ),
    icon: <CalendarOutlined />,
    roles: DEFAULT_ROLES,
    visible: (ctx) => ctx.authResult.use_scheduler,
  },
  {
    key: '/admin',
    label: 'Admin',
    icon: <UserOutlined />,
    roles: [UserRole.ADMIN, UserRole.ORGANIZATION, UserRole.PROGRAM_ADMINISTRATION, UserRole.SITE],
    hideOnMobile: true,
    children: (ctx) => {
      const { role } = ctx.currentUser;

      return [
        { label: 'Clients', key: '/admin-clients' },
        { label: 'Clinicians', key: '/admin-clinicians' },
        { label: 'Organizations', key: '/admin-organizations' },
        { label: 'Products', key: '/admin-products' },
        { label: 'Sites', key: '/admin-sites' },
      ].filter((c) => {
        switch (c.key) {
          case '/admin-clients':
            return role !== UserRole.PROGRAM_ADMINISTRATION;
          case '/admin-organizations':
          case '/admin-products':
            return role === UserRole.ADMIN;
          case '/admin-sites':
            return role === UserRole.ADMIN || role === UserRole.ORGANIZATION;
          default:
            return true;
        }
      });
    },
  },
  { key: '/program-dashboard', label: 'Program dashboard', icon: <BarChartOutlined />, roles: [UserRole.SITE] },
  {
    key: '/metrics',
    label: 'Metrics',
    icon: <BarChartOutlined />,
    roles: [UserRole.ADMIN, UserRole.ORGANIZATION, UserRole.PROGRAM_ADMINISTRATION],
    hideOnMobile: true,
    children: (ctx) => {
      const isAdmin = ctx.currentUser.role === UserRole.ADMIN;
      const isProgramAdmin = ctx.currentUser.role === UserRole.PROGRAM_ADMINISTRATION;

      return [
        { label: isAdmin ? 'Program dashboard TA' : 'Program dashboard', key: '/program-dashboard' },
        { label: isAdmin ? 'Program dashboard OLH' : 'Program dashboard', key: '/program-dashboard-olh' },
        { label: 'Clinician dashboard', key: '/clinician-dashboard' },
        { label: 'Reports', key: '/report' },
        { label: 'Clinician notes', key: '/interventions' },
        { label: 'Inactive clients', key: '/inactive-clients' },
      ].filter((c) => {
        switch (c.key) {
          case '/program-dashboard':
            return isAdmin || ctx.currentUser.organization_id !== UserOrganization.OLH;
          case '/program-dashboard-olh':
            return isAdmin || ctx.currentUser.organization_id === UserOrganization.OLH;
          case '/clinician-dashboard':
            return !isAdmin && !isProgramAdmin && ctx.authResult.use_clinician_dashboard;
          case '/interventions':
            return isAdmin || (!isProgramAdmin && ctx.authResult.use_actions);
          default:
            return true;
        }
      });
    },
  },
  {
    key: '/client-onboarding',
    label: 'Add client',
    icon: <UserAddOutlined />,
    roles: ALL_ROLES,
    visible: (ctx) =>
      ctx.currentUser.role === UserRole.ADMIN || ctx.currentUser.organization_id !== UserOrganization.OLH,
  },
  {
    key: '/resources',
    label: 'Resources',
    icon: <BulbOutlined />,
    roles: ALL_ROLES,
    visible: (ctx) =>
      ctx.currentUser.role === UserRole.ADMIN || ctx.currentUser.organization_id !== UserOrganization.OLH,
  },
  { key: '/clinician-settings', label: 'My settings', icon: <SettingOutlined />, roles: DEFAULT_ROLES },
  { key: '/admin-add-product', label: 'Add product', icon: <ProductOutlined />, roles: [UserRole.ADMIN] },
  { key: '/logout', label: 'Log out', icon: <LogoutOutlined />, roles: ALL_ROLES },
];

const buildMenuItem = (def: SideMenuItemDef, ctx: MenuCtx) => {
  if (!ctx.currentUser.role || !def.roles.includes(ctx.currentUser.role)) return null;
  if (def.visible && !def.visible(ctx)) return null;
  if (def.hideOnMobile && isMobile) return null;

  const label = typeof def.label === 'function' ? def.label(ctx) : def.label;
  const children = def.children?.(ctx);

  return { key: def.key, label, icon: def.icon, ...(children ? { children } : {}) };
};

export const getSideMenuItems = (
  currentUser: CurrentUser,
  authResult: AuthResult,
  missedScheduledProductCount: number,
): MenuProps['items'] => {
  const ctx: MenuCtx = { currentUser, authResult, missedScheduledProductCount };
  return SIDE_MENU_ITEMS.map((def) => buildMenuItem(def, ctx)).filter((item) => item !== null);
};
